import { ReactNode } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { buttonIconMotionSx, softButtonSx } from 'components/ui/styleKit';
import { freezeLivingFillSx } from 'features/subscription-freeze/components/freezeMotion';
import { TEMPERATURE } from 'features/subscription-freeze/components/freezeUi';
import { inkButtonSx, machinedButtonSx } from 'sections/get-started/inkButtonSx';

/**
 * ink = the drawer's commit button, inverse = the same for a dark card, soft/neutral/dangerSoft = the billing
 * tables' tint, danger = ink's red twin, ghost = a quiet step back, ice = the pause's own button.
 */
export type ButtonTone = 'ink' | 'inverse' | 'soft' | 'neutral' | 'danger' | 'dangerSoft' | 'ghost' | 'ice';

const ICE = TEMPERATURE.ice;

const TONE_SX: Record<ButtonTone, (theme: Theme) => object> = {
  ink: (theme) => inkButtonSx(theme.palette.mode === 'dark'),
  inverse: () => inkButtonSx(true),
  soft: (theme) => softButtonSx(theme),
  // Read from the text colour, not the grey palette: dark mode's greys are too faint to read as live.
  neutral: (theme) => {
    const ink = theme.palette.text.primary;
    const dark = theme.palette.mode === 'dark';
    return {
      ...softButtonSx(theme, 'secondary'),
      background: alpha(ink, dark ? 0.08 : 0.05),
      borderColor: theme.palette.divider,
      color: ink,
      '&:hover': { background: alpha(ink, dark ? 0.14 : 0.09), borderColor: alpha(ink, 0.2) },
      '&:active': { background: alpha(ink, dark ? 0.18 : 0.12) }
    };
  },
  dangerSoft: (theme) => softButtonSx(theme, 'error'),
  ghost: (theme) => ({
    ...softButtonSx(theme, 'secondary'),
    background: 'transparent',
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    '&:hover': { background: theme.palette.action.hover, borderColor: theme.palette.divider, color: theme.palette.text.primary }
  }),
  // The freeze page's moving ice fill, so choosing a pause already looks like the freeze it starts.
  ice: () => ({
    ...freezeLivingFillSx('ice'),
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    border: `1px solid ${ICE.accent}`,
    boxShadow: `0 6px 18px -8px ${alpha(ICE.accent, 0.9)}`,
    '&:hover': { boxShadow: `0 8px 24px -6px ${ICE.accent}`, filter: 'brightness(1.05)' },
    '&:active': { transform: 'translateY(1px)' },
    '&.Mui-disabled': { color: alpha(ICE.ink, 0.5), boxShadow: 'none', opacity: 0.55, animation: 'none' }
  }),
  danger: (theme) => {
    const { main, dark, contrastText } = theme.palette.error;
    return machinedButtonSx({
      bg: main,
      hover: dark,
      text: contrastText,
      disabledBg: alpha(main, 0.35),
      disabledText: alpha(contrastText, 0.85)
    });
  }
};

export type ToneButtonProps = {
  label: string;
  onClick: () => void;
  tone: ButtonTone;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  /** Swaps the leading icon for a spinner and holds the button. */
  loading?: boolean;
  fullWidth?: boolean;
};

/** The cancellation surfaces' one button: the app's two button recipes, each with an icon that answers the pointer. */
export default function ToneButton({ label, onClick, tone, startIcon, endIcon, disabled, loading, fullWidth }: ToneButtonProps) {
  return (
    <Button
      variant="contained"
      disableElevation
      fullWidth={fullWidth}
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : startIcon}
      endIcon={endIcon}
      sx={(theme) => ({
        ...TONE_SX[tone](theme),
        ...buttonIconMotionSx,
        transition: 'background-color .18s ease, border-color .18s ease, box-shadow .18s ease, transform .1s ease',
        minHeight: 40,
        px: 2,
        whiteSpace: 'nowrap'
      })}
    >
      {label}
    </Button>
  );
}
